/**
 * ⟦A-4 décision 13⟧ **Les pop-ups de palier, avec les VRAIS chiffres du vrai YouTube.**
 *
 * « Aux paliers, des pop-ups "bravo" avec les VRAIS pourcentages du YouTube réel. » Les
 * chiffres viennent de [RECHERCHE-NARRATIF §2.16](../../../docs/RECHERCHE-NARRATIF.md),
 * sourcés 2025-2026 — ils ne sont ni arrondis à la louche ni inventés, et c'est tout
 * l'intérêt : **le jeu parle du vrai YouTube**, et le joueur mesure ce qu'il vient de faire.
 *
 * Un palier passe **avant** un déverrouillage et avant un événement : c'est le plus gros
 * moment des trois, il ne se retrouve pas coincé derrière une tuile de bad buzz.
 *
 * ## ⟦C57 · lot 3 · A-174⟧ L'icône, le chiffre en gros, UNE phrase
 *
 * À l'écran il ne reste que la plaque (l'icône et le chiffre en gros : « 1 000 abonnés »), UNE
 * phrase de 12 mots au plus (`court`) et « Continuer ». Le texte aux chiffres réels, mot pour mot
 * (`texte`, `game/paliers.ts`), s'ouvre par « Les dessous » et rejoint la ligne du palier dans le
 * Journal (`game/journal.ts`) : rien n'est réécrit, tout est relisible.
 *
 * ⚠️ Ce n'est plus `dialog.info` (le dialogue maison du store, qui ne porte qu'un texte) : la
 * pop-up est une `Fenetre` à elle, comme `RecapJours`, pour pouvoir poser le bouton « Les dessous ».
 * Les autres surfaces n'ont pas besoin de savoir qu'un dialogue est ouvert : elles attendent
 * déjà TOUTES `paliersEnAttente` (`ModeDecouverte`, `InfoBulleDecouverte`, `MomentsDebloques`,
 * `EvenementModal`, `useMattChambre`).
 */

import { useEffect, useRef } from "react";

import { chiffreDuPalier } from "../game/paliers";
import { nombre } from "../game/format";
import { useDecouverteStore } from "../stores/decouverteStore";
import { useRunStore } from "../stores/runStore";
import { useUIStore } from "../stores/uiStore";
import { DessousBloc, LesDessous } from "./LesDessous";
import { Fenetre } from "./Fenetre";
import { useSfx } from "./SoundProvider";

export function PopupsPalier() {
  const file = useRunStore((s) => s.paliersEnAttente);
  const consommer = useRunStore((s) => s.consommerPalier);
  // La chute passe devant : on ne fête pas un palier juste après s'être écroulé.
  const chute = useRunStore((s) => s.chute);
  // ⟦A-9⟧ Une leçon en cours fige le jeu : le palier attend qu'elle soit lue. Le guetteur du
  // mode découverte fait l'inverse (il attend qu'aucun dialogue ne soit ouvert), donc les deux
  // ne peuvent pas se recouvrir.
  const decouverte = useDecouverteStore((s) => s.courante) !== null;
  // ⟦A-16⟧ Et le récapitulatif des jours écoulés passe devant : la célébration se comprend
  // mieux quand on vient de lire ce que ces trois jours ont coûté.
  const recap = useRunStore((s) => s.recapEnAttente) !== null;
  const suspendus =
    useUIStore((s) => s.momentsSuspendus) || chute !== null || decouverte || recap;
  const sfx = useSfx();
  const prochain = file[0];
  const bouton = useRef<HTMLButtonElement>(null);
  const visible = prochain !== undefined && !suspendus;

  useEffect(() => {
    if (prochain === undefined || suspendus) return;
    // Les deux plaques (Bouton d'Argent, Bouton d'Or) ont leur propre son (§7.6).
    sfx(prochain >= 100_000 ? "plaque_jingle" : "palier_jingle");
    sfx("dialog_open");
    bouton.current?.focus();
  }, [prochain, suspendus, sfx]);

  // Échap = « Continuer », comme l'ancien dialogue maison. Le dialogue « Les dessous » arrête la
  // propagation de SON Échap (phase de capture) : refermer les dessous ne referme pas le palier.
  useEffect(() => {
    if (!visible) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        consommer();
      }
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [visible, consommer]);

  if (!visible || prochain === undefined) return null;
  const chiffre = chiffreDuPalier(prochain, nombre);

  return (
    <div className="overlay" role="presentation">
      <div className="dialogue-fenetre">
        <Fenetre
          titre="Palier franchi"
          icone={chiffre.icone}
          role="dialog"
          ariaModal
          className="fenetre-dialogue fenetre-palier"
          pied={
            <button type="button" ref={bouton} className="bouton primaire" onClick={consommer}>
              Continuer
            </button>
          }
        >
          {/* Le chiffre en gros : c'est le palier lui-même, pas une phrase. */}
          <p className="palier-gros">
            <span className="palier-icone" aria-hidden="true">
              {chiffre.icone}
            </span>
            {chiffre.titre}
          </p>
          <p className="dialogue-corps palier-court">{chiffre.court}</p>
          <LesDessous titre={chiffre.titre} className="palier-dessous">
            <DessousBloc titre="Les vrais chiffres">
              <p className="dessous-texte">{chiffre.texte}</p>
            </DessousBloc>
          </LesDessous>
        </Fenetre>
      </div>
    </div>
  );
}
